/**
 * Client-side RL aggregate (hybrid GL × MEDDPICC + median fallback).
 */

const DEFAULT_MEDDPICC_POWER = {
  economic_buyer: 5,
  decision_maker: 4,
  champion: 4,
  executive: 5,
  influencer: 3,
  technical_buyer: 3,
  coach: 3,
  user: 2,
  metrics: 2,
  competition: 2,
  unknown: 3,
};

function meddpiccPower(role, map = DEFAULT_MEDDPICC_POWER, unknownWeight = 3) {
  const key = String(role || "unknown")
    .toLowerCase()
    .replace(/\s+/g, "_");
  if (!key || key === "unknown") return Number(unknownWeight) || 3;
  return map[key] ?? (Number(unknownWeight) || 3);
}

function clampRl(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.min(5, Math.round(n)));
}

export function calculateHybridRlAggregate(items, settings = {}) {
  const powerMap = {
    ...DEFAULT_MEDDPICC_POWER,
    ...(settings.meddpicc_power_map || {}),
  };
  const glMap = settings.gl_weight_map || { 1: 1, 2: 2, 3: 3, 4: 4, 5: 5 };
  const considerMeddpiccRole = settings.consider_meddpicc_role !== false;
  const unknownWeight = Number(settings.unknown_meddpicc_default_weight);
  const effectiveUnknownWeight = Number.isFinite(unknownWeight)
    ? Math.max(1, Math.min(5, Math.round(unknownWeight)))
    : 3;

  const scored = (items || []).filter(
    i => i.rlScore !== null && i.rlScore !== undefined
  );
  if (!scored.length) return null;

  let useMedian = false;
  let sumWeighted = 0;
  let sumWeights = 0;

  for (const item of scored) {
    const gl = Number(item.vendorGl ?? item.ladderLevel);
    const power = considerMeddpiccRole
      ? (item.powerLevel ??
        meddpiccPower(item.meddpiccRole, powerMap, effectiveUnknownWeight))
      : effectiveUnknownWeight;
    if (!Number.isFinite(gl) || gl < 1 || !Number.isFinite(power)) {
      useMedian = true;
      break;
    }
    const glWeight = glMap[gl] ?? gl;
    const weight = glWeight * power;
    sumWeighted += clampRl(item.rlScore) * weight;
    sumWeights += weight;
  }

  if (useMedian || sumWeights === 0) {
    const values = scored
      .map(i => clampRl(i.rlScore))
      .filter(v => v !== null)
      .sort((a, b) => a - b);
    if (!values.length) return null;
    const mid = Math.floor(values.length / 2);
    return values.length % 2
      ? values[mid]
      : Math.round(((values[mid - 1] + values[mid]) / 2) * 10) / 10;
  }

  return Math.round((sumWeighted / sumWeights) * 10) / 10;
}

export function repairOrganizationSettings(raw) {
  if (!raw) return {};
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? repairOrganizationSettings(parsed)
        : {};
    } catch {
      return {};
    }
  }
  if (typeof raw !== "object" || Array.isArray(raw)) return {};
  return raw;
}

export function getOrgSettings(organization) {
  return repairOrganizationSettings(organization?.settings);
}

export function coerceOrgBoolean(value, defaultValue = false) {
  if (value === true || value === "true") return true;
  if (value === false || value === "false") return false;
  return defaultValue;
}

export function isRlAssessmentsEnabledForOrg(organization) {
  const settings = getOrgSettings(organization);
  if (
    Object.prototype.hasOwnProperty.call(settings, "relationship_assessments")
  ) {
    return coerceOrgBoolean(settings.relationship_assessments, false);
  }
  return coerceOrgBoolean(settings.rl_assessments_enabled, false);
}

export function isConsiderMeddpiccRoleEnabledForOrg(organization) {
  return getOrgSettings(organization).consider_meddpicc_role !== false;
}
